import { useState } from "react";
import { api } from "../api.js";
import { useApp } from "../app.jsx";
import { navigate } from "../router.jsx";
import { money, fmtDateShort } from "../format.js";
import {
  Async, Avatar, Badge, Button, Card, Chips, Empty, ErrorPanel, Field, Fields, Grid, Input, KPI,
  Modal, Select, Table, Textarea, TopBar, Wrap, useAsync, useToast,
} from "../ui.jsx";

const termsLabel = (days) => (Number(days) ? `Net ${Number(days)}` : "Due on receipt");

export default function Clients() {
  const { settings, reloadApp, me } = useApp();
  const toast = useToast();
  const state = useAsync(() => api.get("clients"), []);
  const [filter, setFilter] = useState("active");
  const [creating, setCreating] = useState(false);
  const sym = settings.currency_symbol;

  return (
    <>
      <TopBar title="Clients">
        <Button
          onClick={() =>
            api.download("exports/clients", "clients.csv").catch((e) => toast.push(e.message, "err"))
          }
        >
          ⤓ Export CSV
        </Button>
        {me.permissions["clients.manage"] && (
          <Button kind="pri" onClick={() => setCreating(true)}>
            ＋ New client
          </Button>
        )}
      </TopBar>

      <Wrap>
        <Async state={state}>
          {(all) => {
            const active = all.filter((c) => c.status === "active");
            const archived = all.filter((c) => c.status === "archived");
            const receivable = all.reduce((s, c) => s + Number(c.outstanding || 0), 0);
            const lifetime = all.reduce((s, c) => s + Number(c.lifetime_paid || 0), 0);
            const owing = all.filter((c) => Number(c.outstanding || 0) > 0).length;
            const atRisk = all.filter((c) => c.has_overdue).length;
            const rows = filter === "all" ? all : filter === "archived" ? archived : active;

            return (
              <>
                <Grid cols={4}>
                  <KPI label="Active clients" value={active.length} sub={`${archived.length} archived`} />
                  <KPI
                    label="Total receivable"
                    value={money(receivable, sym)}
                    sub={`across ${owing} client${owing === 1 ? "" : "s"}`}
                  />
                  <KPI label="Lifetime billed" value={money(lifetime, sym)} sub={`${all.length} clients on file`} />
                  <KPI
                    label="At risk"
                    value={atRisk}
                    tone={atRisk ? "warn" : undefined}
                    sub="clients with overdue balances"
                  />
                </Grid>

                <Card
                  className="mt"
                  bare
                  title="All clients"
                  right={
                    <Chips
                      value={filter}
                      onChange={setFilter}
                      options={[
                        { key: "active", label: "Active", count: active.length },
                        { key: "archived", label: "Archived", count: archived.length },
                        { key: "all", label: "All", count: all.length },
                      ]}
                    />
                  }
                >
                  <Table
                    rows={rows}
                    onRowClick={(c) => navigate(`/clients/${c.id}`)}
                    empty={<Empty icon="◍">No {filter === "all" ? "" : filter} clients yet</Empty>}
                    columns={[
                      {
                        key: "name",
                        label: "Client",
                        render: (c) => (
                          <div className="stack">
                            <Avatar name={c.name} />
                            <div>
                              <b>{c.name}</b>
                              {c.status === "archived" && (
                                <>
                                  {" "}
                                  <Badge status="archived" />
                                </>
                              )}
                              <div className="tiny muted">Client since {fmtDateShort(c.since)}</div>
                            </div>
                          </div>
                        ),
                      },
                      {
                        key: "contact",
                        label: "Primary contact",
                        render: (c) => (
                          <>
                            <div className="sm">{c.contact_name || "—"}</div>
                            <div className="tiny muted">{c.email || "No email on file"}</div>
                          </>
                        ),
                      },
                      { key: "terms", label: "Terms", render: (c) => <span className="sm dim">{termsLabel(c.terms_days)}</span> },
                      {
                        key: "invoices",
                        label: "Invoices",
                        align: "right",
                        render: (c) => <span className="sm dim num">{Number(c.invoice_count || 0)}</span>,
                      },
                      {
                        key: "outstanding",
                        label: "Outstanding",
                        align: "right",
                        render: (c) =>
                          Number(c.outstanding || 0) ? (
                            <b className="num" style={c.has_overdue ? { color: "var(--danger)" } : undefined}>
                              {money(c.outstanding, sym)}
                            </b>
                          ) : (
                            <span className="muted">—</span>
                          ),
                      },
                      {
                        key: "lifetime",
                        label: "Lifetime",
                        align: "right",
                        render: (c) => <span className="num dim">{money(c.lifetime_paid, sym)}</span>,
                      },
                      { key: "go", label: "", align: "right", render: () => <span className="muted">›</span> },
                    ]}
                  />
                </Card>
              </>
            );
          }}
        </Async>
      </Wrap>

      {creating && (
        <NewClientModal
          onClose={() => setCreating(false)}
          onSaved={() => {
            state.reload();
            reloadApp();
          }}
        />
      )}
    </>
  );
}

/* -------------------------------------------------------------- new client */

function NewClientModal({ onClose, onSaved }) {
  const { settings } = useApp();
  const toast = useToast();
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    terms_days: settings.default_terms_days ?? 30,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  async function save() {
    setError(null);
    setBusy(true);
    try {
      const client = await api.post("clients", { ...form, terms_days: Number(form.terms_days) });
      toast.push(`${client.name} added`);
      onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="New client"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="pri" busy={busy} onClick={save}>
            Add client
          </Button>
        </>
      }
    >
      <Fields>
        {error && <ErrorPanel error={error} />}
        <Fields cols={2}>
          <Field label="Business name">
            <Input value={form.name} placeholder="Acme Inc." onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Primary contact">
            <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
          </Field>
        </Fields>
        <Fields cols={2}>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </Field>
        </Fields>
        <Field label="Billing address">
          <Textarea rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} />
        </Field>
        <Field label="Payment terms">
          <Select value={form.terms_days} onChange={(e) => set("terms_days", e.target.value)}>
            <option value={15}>Net 15</option>
            <option value={30}>Net 30</option>
            <option value={45}>Net 45</option>
            <option value={0}>Due on receipt</option>
          </Select>
        </Field>
        <Field label="Internal note">
          <Textarea
            rows={2}
            value={form.notes}
            placeholder="Only your team sees this"
            onChange={(e) => set("notes", e.target.value)}
          />
        </Field>
      </Fields>
    </Modal>
  );
}
